
import { useState } from 'react';
import { Menu, X, LogOut, User, Building, Handshake, UserPlus } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AppSettings } from '@/components/settings/AppSettings';
import { BrandLogo } from '@/components/ui/brand-logo';

export const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, userRole, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  
  const closeMenu = () => setIsOpen(false);

  // Hide navigation on admin routes
  if (location.pathname.startsWith('/admin')) {
    return null;
  }

  return (
    <nav className="fixed w-full bg-primary/95 backdrop-blur-lg shadow-lg z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <BrandLogo />
          </div>
          
          <div className="hidden md:flex items-center space-x-6">
            {isAuthenticated && <AppSettings />}
            {isAuthenticated ? (
              <button 
                onClick={logout}
                className="flex items-center gap-1 bg-white text-primary hover:bg-white/90 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 shadow-sm"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </button>
            ) : (
              <button 
                onClick={() => navigate('/login')}
                className="bg-white text-primary hover:bg-white/90 px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 shadow-sm hover:shadow"
              >
                Login
              </button>
            )}
          </div>
          
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-white hover:text-gray-200 focus:outline-none"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>
      
      {isOpen && (
        <div className="md:hidden bg-gradient-to-br from-primary/95 to-primary/90 backdrop-blur-xl border-t border-white/20 shadow-2xl">
          <div className="px-4 pt-4 pb-6 space-y-4">
            {isAuthenticated && (
              <div className="px-3 py-2 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20">
                <AppSettings />
              </div>
            )}
            
            {isAuthenticated ? (
              <div className="space-y-3">
                <div className="px-3 py-1">
                  <h3 className="text-white/70 font-semibold text-xs uppercase tracking-wider">Account</h3>
                </div>
                <button
                  onClick={() => {
                    closeMenu();
                    logout();
                  }}
                  className="w-full flex items-center gap-3 bg-white/10 hover:bg-white/20 text-white border border-white/20 px-4 py-3 rounded-xl text-base font-medium transition-all duration-300 group backdrop-blur-sm shadow-lg hover:shadow-xl hover:scale-[1.02]"
                >
                  <LogOut className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                  <span>Logout</span>
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-3">
                  <div className="px-3 py-1">
                    <h3 className="text-white/70 font-semibold text-xs uppercase tracking-wider">Account Access</h3>
                  </div>
                  
                  <button 
                    onClick={() => {
                      closeMenu();
                      navigate('/login');
                    }}
                    className="w-full flex items-center gap-3 bg-white/10 hover:bg-white/20 text-white border border-white/20 px-4 py-3 rounded-xl text-base font-medium transition-all duration-300 group backdrop-blur-sm shadow-lg hover:shadow-xl hover:scale-[1.02]"
                  >
                    <User className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                    <div className="flex-1 text-left">
                      <div className="font-medium">Login</div>
                      <div className="text-xs text-white/70">Access your account</div>
                    </div>
                  </button>
                  
                  <button 
                    onClick={() => {
                      closeMenu();
                      navigate('/signup');
                    }}
                    className="w-full flex items-center gap-3 bg-white/10 hover:bg-white/20 text-white border border-white/20 px-4 py-3 rounded-xl text-base font-medium transition-all duration-300 group backdrop-blur-sm shadow-lg hover:shadow-xl hover:scale-[1.02]"
                  >
                    <UserPlus className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                    <div className="flex-1 text-left">
                      <div className="font-medium">Sign Up</div>
                      <div className="text-xs text-white/70">Create new account</div>
                    </div>
                  </button>
                </div>

                <div className="border-t border-white/20 pt-4 space-y-3">
                  <div className="px-3 py-1">
                    <h3 className="text-white/70 font-semibold text-xs uppercase tracking-wider">Business Solutions</h3>
                  </div>
                  
                  <button 
                    onClick={() => {
                      closeMenu();
                      navigate('/for-brokers');
                    }}
                    className="w-full flex items-center gap-3 bg-white/10 hover:bg-white/20 text-white border border-white/20 px-4 py-3 rounded-xl text-base font-medium transition-all duration-300 group backdrop-blur-sm shadow-lg hover:shadow-xl hover:scale-[1.02]"
                  >
                    <Building className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                    <div className="flex-1 text-left">
                      <div className="font-medium">For Brokers</div>
                      <div className="text-xs text-white/70">Partner with us</div>
                    </div>
                  </button>
                  
                  <button 
                    onClick={() => {
                      closeMenu();
                      navigate('/for-peos');
                    }}
                    className="w-full flex items-center gap-3 bg-white/10 hover:bg-white/20 text-white border border-white/20 px-4 py-3 rounded-xl text-base font-medium transition-all duration-300 group backdrop-blur-sm shadow-lg hover:shadow-xl hover:scale-[1.02]"
                  >
                    <Building className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                    <div className="flex-1 text-left">
                      <div className="font-medium">For PEOs</div>
                      <div className="text-xs text-white/70">Join our network</div>
                    </div>
                  </button>
                  
                  <button 
                    onClick={() => {
                      closeMenu();
                      navigate('/partnerships');
                    }}
                    className="w-full flex items-center gap-3 bg-white/10 hover:bg-white/20 text-white border border-white/20 px-4 py-3 rounded-xl text-base font-medium transition-all duration-300 group backdrop-blur-sm shadow-lg hover:shadow-xl hover:scale-[1.02]"
                  >
                    <Handshake className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                    <div className="flex-1 text-left">
                      <div className="font-medium">Partnerships</div>
                      <div className="text-xs text-white/70">Strategic alliances</div>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};
